import { eventBus } from '../event-manager';
import {
  disposeLocalTracks,
  useBaseStore,
  useConfigStore,
  useConnectionStore,
  useTracksStore,
} from '../store';
import { useConferenceStore } from '../store/conference';
import { resetAllStores } from '../store/utils/reset';
import { tryCatchSync } from '../utils';
import { Mutex } from '../utils/mutex';
import { connect } from './connection';
import { initDeviceList } from './devices';
import { initializeLib } from './init';
import { createLocalTracks, updateAudioOutputDevice } from './track';

let isSessionStarted = false;
let reconnectTimeoutId: NodeJS.Timeout | null = null;
const RECONNECT_DEBOUNCE_DELAY = 3000;

initializeLib();

export function startSession() {
  const sessionId = useConfigStore.getState().sessionId;
  if (!sessionId) {
    console.error('Session ID is required to start a session');
    return;
  }
  if (isSessionStarted) {
    console.log('Session already started');
    return;
  }
  isSessionStarted = true;
  console.log(`Session started in room: ${sessionId}`);
  createLocalTracks();
  const audioOutputDeviceId =
    useConfigStore.getState().audioOutputDeviceId ??
    useBaseStore.getState().audioOutputDevice?.deviceId;
  if (audioOutputDeviceId) {
    updateAudioOutputDevice(audioOutputDeviceId);
  }
  eventBus.startEmitting();
  const test = tryCatchSync(() => connect());
  if (test.error) {
    console.error('Error connecting to session:', test.error);
    // TODO: Handle connection error
    useConnectionStore.getState().connectionFailed(test.error.message);
    return;
  }
  initDeviceList();
}

async function _leaveSession() {
  if (isSessionStarted === false) {
    return;
  }
  isSessionStarted = false;
  await Promise.all([
    disposeLocalTracks(),
    useConferenceStore.getState().leaveConference(),
  ]);
  await useConnectionStore.getState().disconnect();
}

const sessionMutex = new Mutex();
export function leaveSession(options: { forceLeave?: boolean } = {}) {
  return sessionMutex.run(async () => {
    const isPeerCall = useConfigStore.getState().isPeerCall;
    const shouldEnd = isPeerCall && !options.forceLeave;
    if (shouldEnd) {
      useConferenceStore.getState().endConference();
      return;
    }
    useBaseStore.getState().clearIdealTimeoutTimer();
    cancelPendingReconnect();
    await _leaveSession();
    resetAllStores();
  });
}

export async function leaveSessionDueToIdleTimeout() {
  eventBus.publish({
    type: 'onSessionTimedOut',
  });
  await leaveSession();
}

export async function reconnectSession() {
  // Clear any existing timeout to debounce rapid calls
  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }

  // Debounce the function call
  return new Promise<void>((resolve, reject) => {
    reconnectTimeoutId = setTimeout(async () => {
      try {
        await _reconnectSession();
        resolve();
      } catch (error) {
        reject(error);
      } finally {
        reconnectTimeoutId = null;
      }
    }, RECONNECT_DEBOUNCE_DELAY);
  });
}

export function cancelPendingReconnect() {
  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
    console.log('Cancelled pending reconnect');
  }
}

async function _reconnectSession() {
  const retryCount = useBaseStore.getState().connectionRetryCount;
  if (retryCount >= 3 && isSessionStarted === true) {
    console.warn('Maximum retry attempts reached. Leaving session.');
    await leaveSession();
    return;
  }
  useBaseStore.getState().incrementConnectionRetryCount();
  await _leaveSession();
  useConnectionStore.getState().reset();
  useConferenceStore.getState().reset();
  useTracksStore.getState().reset();
  startSession();
}
